import { Box, Stack } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';

import { Aircraft } from 'domain/entities/Aircraft';
import { Entity } from 'domain/types/Entity';
import { AircraftRepository } from 'domain/repositories/IAircraftRepository';
import { useCaseSearchAircraft } from 'application/aircraft/useCases/useCaseSearchAircraft';
import { useCaseCreateAircraft } from 'application/aircraft/useCases/useCaseCreateAircraft';
import { useHookInjection } from 'application/hooks';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { Breadcrumbs, Button, TableColumn, TableWithSearch } from 'targets/web/components';
import { OfflinePlaceholder, PageTitle } from 'targets/web/modules/dashboard/components';
import { configurationRoutes } from 'targets/web/modules/configuration/navigation/configurationRoutes';
import {
  AircraftViewModal,
  CreateAircraftData,
  CreateAircraftModal,
  DuplicateRegistrationWarningDialog,
} from 'targets/web/modules/configuration/components';
import useSnackbar from 'targets/web/modules/dashboard/hooks/useSnackbar';
import { useNetworkStatus } from 'targets/web/modules/dashboard/hooks/useNetworkStatus';

export const AircraftConfigurationView: FC = () => {
  const notify = useSnackbar();
  const t = useTranslationPrefix('configuration.aircraft');
  const tBreadcrumbs = useTranslationPrefix('configuration.breadcrumbs');
  const { isOffline } = useNetworkStatus();

  const [aircraftPhrase, setAircraftPhrase] = useState('');
  const [isCreateAircraftModalOpen, setIsCreateAircraftModalOpen] = useState(false);
  const [selectedAircraft, setSelectedAircraft] = useState<Entity<Aircraft> | null>(null);
  const [pendingCreate, setPendingCreate] = useState<{
    values: CreateAircraftData;
    duplicates: Entity<Aircraft>[];
  } | null>(null);

  const {
    items = [],
    total,
    isLoading,
    isFetching,

    paging,
    changePage,
    changePageSize,

    sorting,
    changeSort,
  } = useCaseSearchAircraft({ phrase: aircraftPhrase });
  const { createAircraft } = useCaseCreateAircraft();
  const { searchAircraft } = useHookInjection<AircraftRepository>(AircraftRepository);

  const columns: TableColumn<Aircraft>[] = useMemo(
    () => [
      {
        headerName: t('column_header.aircraft_type'),
        field: 'type',
        sortable: true,
        width: '25%',
        renderCell: (row) => (
          <Box sx={{ opacity: row.isActive === false ? 0.4 : 1 }}>{row.type.name}</Box>
        ),
      },
      {
        headerName: t('column_header.reg_number'),
        field: 'code',
        sortable: true,
        width: '25%',
        renderCell: (row) => (
          <Box sx={{ opacity: row.isActive === false ? 0.4 : 1 }}>{row.code}</Box>
        ),
      },
      {
        headerName: t('column_header.serial_number'),
        field: 'serialNumber',
        sortable: true,
        width: '25%',
        renderCell: (row) => (
          <Box sx={{ opacity: row.isActive === false ? 0.4 : 1 }}>{row.serialNumber}</Box>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <Stack direction="row" width={1} justifyContent="flex-start" alignItems="center" gap={4}>
        <Stack direction="column" alignItems="flex-start" justifyContent="space-between" flex={1}>
          <Breadcrumbs
            items={[
              {
                label: tBreadcrumbs('configuration'),
                url: configurationRoutes.configuration,
              },
            ]}
          />

          <PageTitle title={t('title')} />
        </Stack>

        <Button
          size="large"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateAircraftModalOpen(true)}
        >
          {t('create_button')}
        </Button>
      </Stack>

      <CreateAircraftModal
        open={isCreateAircraftModalOpen}
        onClose={() => setIsCreateAircraftModalOpen(false)}
        onSubmit={async (values) => {
          const result = await searchAircraft({ phrase: values.regNumber, activeOnly: false });
          const dupes = (result?.items ?? []).filter((a) => a.code === values.regNumber);
          if (dupes.length > 0) {
            setPendingCreate({ values, duplicates: dupes });
            throw new Error('duplicate');
          }
          await createAircraft({
            typeId: values.type.id,
            serialNumber: values.serialNumber,
            regNumber: values.regNumber,
          }).then(
            () => notify(t('snackbar.create_success'), { variant: 'success' }),
            () => notify(t('snackbar.create_error'), { variant: 'error' }),
          );
        }}
      />

      <DuplicateRegistrationWarningDialog
        open={!!pendingCreate}
        duplicates={pendingCreate?.duplicates ?? []}
        onCancel={() => setPendingCreate(null)}
        onProceed={async () => {
          if (!pendingCreate) return;
          const { values } = pendingCreate;
          setPendingCreate(null);
          await createAircraft({
            typeId: values.type.id,
            serialNumber: values.serialNumber,
            regNumber: values.regNumber,
          }).then(
            () => {
              notify(t('snackbar.create_success'), { variant: 'success' });
              setIsCreateAircraftModalOpen(false);
            },
            () => notify(t('snackbar.create_error'), { variant: 'error' }),
          );
        }}
      />

      {selectedAircraft && (
        <AircraftViewModal
          open={!!selectedAircraft}
          aircraft={selectedAircraft}
          onClose={() => setSelectedAircraft(null)}
        />
      )}

      <Stack width={1}>
        {isOffline && !items.length ? (
          <OfflinePlaceholder />
        ) : (
          <TableWithSearch
            onSearchPhraseChange={setAircraftPhrase}
            columns={columns}
            rows={items}
            count={total}
            rowsPerPageOptions={[10, 50, 100]}
            initialRowsPerPage={10}
            orderBy={sorting.sortBy}
            orderDirection={sorting.direction}
            initialPage={Math.round(paging.offset / paging.limit)}
            isLoading={isLoading || isFetching}
            searchPhrasePlaceholder={t('placeholder')}
            onRowsPerPageChange={changePageSize}
            onPageChange={changePage}
            onSortChange={changeSort}
            onRowClick={(row) => setSelectedAircraft(row as Entity<Aircraft>)}
            sx={{ cursor: 'pointer' }}
          />
        )}
      </Stack>
    </>
  );
};
